import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReadPost } from './PostActivityTracker';
import * as styles from './PostDetailPreview.module.scss';
import PostDetailPreviewStateBar, {
  hasStateBar,
} from './PostDetailPreviewStateBar';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from './postPolicyCheck';
import PostPreviewQuillComponent from './PostPreviewQuillComponent';
import { parseShareSecretFromParams } from './useShareSecret';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import InaccessibleMentionNotice from '../editor/mentions/InaccessibleMentionNotice';
import QuillContext from '../editor/QuillContext';
import { gql } from '../graphql/generated/gql';
import { UserRole } from '../topic/topicPolicyCheck';
import { useAllTopicMap } from '../topic/useAllTopics';
import NestableLink from '../utils/NestableLink';
import populateObjects from '../utils/populateObjects';
import PostTopicListWithBreadcrumbs from './topic/PostTopicListWithBreadcrumbs';
import PostLayoutSizeProvider from '../utils/responsive/PostLayoutSizeProvider';
import { getCurrentPostLayoutSize } from '../utils/responsive/Sizes';
import postPath, { publicPostPath } from '../utils/url/postPath';
import useIsPublicView from '../utils/useIsPublicView';

const MIN_ACTIVITY_READ = 10_000;

gql(`
  fragment PostDetailPreview_post on PostFields {
    id
    content {
      id
      delta
    }
    topics {
      id
    }
    ... on Post {
      banner {
        original
      }
    }
    ... on PublicPost {
      banner {
        original
      }
    }
  }
`);

const POST_DETAIL_PREVIEW_QUERY = gql(/** @complexity 10 */ `
  query PostDetailPreview(
    $id: ID!
    $queryId: String
    $resultId: String
    $position: Int
    $shareSecret: String!
  ) {
    post(
      id: $id
      view: true
      queryId: $queryId
      resultId: $resultId
      position: $position
      shareSecret: $shareSecret
    ) {
      id
      ...PostDetailPreview_post
      ...isAllowedThroughLinkAccess_post
      ...postPath_post
      ...PostDetailPreviewStateBar_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

const PUBLIC_POST_DETAIL_PREVIEW_QUERY = gql(/** @complexity 7 */ `
  query PublicPostDetailPreview($id: ID!, $shareSecret: String!) { 
    post: publicPost(id: $id, shareSecret: $shareSecret) {
      id
      ...PostDetailPreview_post
      ...isAllowedThroughLinkAccess_post
      ...publicPostPath_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface PostDetailPreviewBannerProps {
  banner: string;
}

export const PostDetailPreviewBanner = ({
  banner,
}: PostDetailPreviewBannerProps) => (
  <div
    className={styles.banner}
    style={{ backgroundImage: `url(${banner})` }}
  />
);

type PostDetailPreviewProps = {
  onPreviewLoadChange?: (loaded: boolean) => void;
  onClick?: () => void;
  queryId?: string;
  resultId?: string;
  position?: number;
  postId: string;
  anchor?: string;
  open: boolean;
  params?: string;
};

const PostDetailPreview = ({
  postId,
  anchor,
  onPreviewLoadChange,
  onClick,
  queryId,
  resultId,
  position,
  open,
  params,
}: PostDetailPreviewProps) => {
  const isPublicView = useIsPublicView();
  const postContentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>();
  const [postLayoutSize, setPostLayoutSize] =
    useState<ReturnType<typeof getCurrentPostLayoutSize>>('mobile');

  const shareSecret = parseShareSecretFromParams(params) ?? postId;

  const readPost = useReadPost();

  const { data: postData, loading: isDataLoading } = useQuery(
    POST_DETAIL_PREVIEW_QUERY,
    {
      variables: { id: postId ?? '', queryId, resultId, position, shareSecret },
      skip: !postId || isPublicView,
    },
  );

  const { data: publicPost, loading: isPublicDataLoading } = useQuery(
    PUBLIC_POST_DETAIL_PREVIEW_QUERY,
    {
      variables: { id: postId ?? '', shareSecret },
      skip: !postId || !isPublicView,
    },
  );

  const isLoading = isPublicView ? isPublicDataLoading : isDataLoading;

  const data = postData ?? publicPost;

  const topicMap = useAllTopicMap();
  const post = data?.post;
  const topics = populateObjects(
    topicMap,
    post?.topics.map(({ id }) => id),
  );

  const content = post?.content.delta;

  const bannerImage = post?.banner?.original;

  useLayoutEffect(() => {
    if (postContentRef.current) {
      const recalculate = () => {
        const contentEl = postContentRef?.current;
        setContentHeight(contentEl?.getBoundingClientRect()?.height);

        if (contentEl) {
          setPostLayoutSize(
            // we use clientWidth since it's giving us the value before scaling
            getCurrentPostLayoutSize({ baseWidth: contentEl.clientWidth }),
          );
        }
      };
      const resizeObserver = new ResizeObserver(recalculate);
      resizeObserver.observe(postContentRef.current);
      recalculate();

      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [content]);

  const onPreviewLoadChangeRef = useRef(onPreviewLoadChange);

  useLayoutEffect(() => {
    onPreviewLoadChangeRef.current = onPreviewLoadChange;
  }, [onPreviewLoadChange]);

  useEffect(() => {
    onPreviewLoadChangeRef.current?.(!isLoading);
  }, [isLoading]);

  useEffect(() => {
    const readStart = Date.now();

    return () => {
      const time = Date.now() - readStart;
      if (open && time >= MIN_ACTIVITY_READ) {
        readPost({ postId, time, queryId, resultId });
      }
    };
  }, [open, readPost, postId, queryId, resultId]);

  if (!post && !isLoading) {
    return <InaccessibleMentionNotice mention="post" />;
  }

  if (!post) {
    return null;
  }

  const isPubliclyAccessible = post
    ? isAllowedThroughLinkAccess(
        PostPolicyAction.ViewPost,
        post,
        UserRole.Anonymous,
      )
    : false;

  const path =
    isPublicView && isPubliclyAccessible
      ? publicPostPath(post, { anchor })
      : postPath(post, { anchor });

  return (
    <div className={styles.container}>
      <NestableLink
        ignoreInsideLinks
        className={styles.link}
        to={path}
        onClick={onClick}
      >
        {post && post.__typename === 'Post' && (
          <PostDetailPreviewStateBar post={post} />
        )}
        {bannerImage && <PostDetailPreviewBanner banner={bannerImage} />}
        <div
          className={styles.postContentContainer}
          style={{ height: contentHeight }}
        >
          <div
            ref={postContentRef}
            className={classNames(styles.postContent, {
              [styles.attached]:
                ((post.__typename === 'Post' && hasStateBar(post)) ||
                  bannerImage) &&
                topics.length > 0,
            })}
          >
            <PostLayoutSizeProvider postLayoutSize={postLayoutSize}>
              <PostTopicListWithBreadcrumbs
                postId={postId}
                postTopics={topics}
                editing={false}
              />
            </PostLayoutSizeProvider>
            <QuillContext>
              <AssetTokenProvider entityAssetToken={data.entityAssetToken}>
                <PostPreviewQuillComponent
                  content={content}
                  anchor={anchor}
                  rootClassName={styles.editorRoot}
                />
              </AssetTokenProvider>
            </QuillContext>
          </div>
        </div>
      </NestableLink>
    </div>
  );
};

export default PostDetailPreview;
